import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { useDashboardData } from '../useDashboardData';
import { TileHint } from '../TileFrame';
import { DASH_COLORS, DASH_FONTS, DASH_TYPE } from '../dashboardTokens';
import { groupFeedEvents } from '../feedGrouping';
import AiAvatar from '../AiAvatar';

// Activity feed (#1160, #1261, specs/workshop-events.md): the workshop's
// heartbeat as content-free lines — what kind of thing happened and when,
// never what was written, never a name. Every line is a workshop event;
// its label comes from the vocabulary the payload carries (activity.kinds:
// the modules' own terms in fi/en/sv), so a module that records a new kind
// shows up here without a change on this side. Bursts collapse into counted
// lines, and interleaved human and AI work keeps its own line each.

const FeedList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: clamp(4px, 0.7vmin, 16px);
`;

// Chat analogue (decision 2026-10-19): human events sit left, AI events
// right with the AI symbol as avatar.
const FeedRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: ${({ $ai }) => ($ai ? 'flex-end' : 'flex-start')};
  gap: clamp(8px, 1vmin, 22px);
  font-size: ${DASH_TYPE.meta};
  color: ${DASH_COLORS.text};
  text-align: ${({ $ai }) => ($ai ? 'right' : 'left')};

  time {
    font-family: ${DASH_FONTS.numeric};
    color: ${DASH_COLORS.textDim};
    flex: 0 0 auto;
  }

  &:nth-of-type(n + 3) { opacity: 0.65; }
  &:nth-of-type(n + 5) { opacity: 0.4; }
`;

// live: single events since the last fetch; bursts: the backfill, counted
function groupedEvents(live, bursts, kinds, limit) {
  const merged = [...(live || []), ...(bursts || [])]
    // A kind recorded but kept off the tile (votes) may still arrive live
    .filter((event) => kinds?.[event.kind]?.feed !== false)
    .sort((a, b) => new Date(b.at) - new Date(a.at));
  return groupFeedEvents(merged, { limit });
}

// The kind's label in the instance language; the raw kind id when a module
// records something its manifest does not name, so the gap is visible.
export function feedLine(kinds, language, group) {
  const entry = kinds?.[group.kind];
  if (!entry) return group.kind;
  const form = group.count > 1 ? 'many' : 'one';
  const label = entry.labels?.[form]?.[language] || entry.labels?.[form]?.fi || group.kind;
  return label.replace('{{count}}', String(group.count));
}

function FeedLines({ t, limit, fade }) {
  const { language } = useTranslation();
  const { data, feedEvents } = useDashboardData();
  const kinds = data?.activity?.kinds;
  const groups = groupedEvents(feedEvents, data?.activity?.bursts, kinds, limit);
  if (!groups.length) return <TileHint>{t('dashboard.noData')}</TileHint>;
  return (
    <FeedList>
      {groups.map((group, i) => {
        const ai = kinds?.[group.kind]?.actor === 'ai' || group.kind.startsWith('ai.');
        return (
          <FeedRow key={`${group.kind}-${i}`} $ai={ai} style={fade ? undefined : { opacity: 1 }}>
            <time>
              {new Date(group.at).toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' })}
            </time>
            <span>{feedLine(kinds, language, group)}</span>
            {ai && <AiAvatar />}
          </FeedRow>
        );
      })}
    </FeedList>
  );
}

export function FeedTileBody({ t }) {
  return <FeedLines t={t} limit={4} fade />;
}

export function FeedTileDetail({ t }) {
  return <FeedLines t={t} limit={30} fade={false} />;
}
